import React, { useRef, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import UserService from "services/UserService";
import { AuthHelper } from "utils/AuthHelper";
import {
  PLEASE_ENTER_VALID_EMAIL,
  CONFIRMATION_CODE_SENT,
  CONFIRMATION_CODE_SENT_FAILED,
  PLEASE_TRY_AGAIN_LATER,
  PASSWORD_RESET_EMAIL_TEXT,
  LABEL_ENTER_EMAIL,
} from "configurations/MessageConstants";
import "styles/PasswordPopup.css";

const EmailPopup = ({
  show,
  resetEmail,
  onClose,
  onShowResetPasswordPopup,
  setResetEmail,
}) => {
  const inputRef = useRef(null);

  const defaultValues = {
    email: "",
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset,
  } = useForm({ defaultValues });

  useEffect(() => {
    if (show && inputRef.current) {
      inputRef.current.focus();
    }
  }, [show]);

  useEffect(() => {
    reset({ email: resetEmail });
  }, [resetEmail, reset]);

  const email = watch("email");
  const handleFormSubmit = async (data) => {
    setResetEmail(email);
    try {
      await UserService.sendResetPasswordCode(data.email);
      handleShowResetPasswordPopup();
    } catch (error) {
      console.error(
        CONFIRMATION_CODE_SENT_FAILED + PLEASE_TRY_AGAIN_LATER,
        error
      );
      toast.error(
        <div>
          {CONFIRMATION_CODE_SENT_FAILED}
          <br />
          {PLEASE_TRY_AGAIN_LATER}
        </div>
      );
    }
  };

  const handleShowResetPasswordPopup = () => {
    toast.success(CONFIRMATION_CODE_SENT);
    onShowResetPasswordPopup();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      className="password-modal"
      centered
      size="md"
      aria-labelledby="forgot-password-popup"
    >
      <Modal.Header closeButton>
        <Modal.Title id="forgot-password-popup">Forgot Password</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3 text-muted">{PASSWORD_RESET_EMAIL_TEXT}</div>
        <Form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
          <Form.Group className="mb-4">
            <Controller
              name="email"
              control={control}
              rules={{
                required: PLEASE_ENTER_VALID_EMAIL,
                validate: (value) =>
                  AuthHelper.isValidEmail(value) || PLEASE_ENTER_VALID_EMAIL,
              }}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  ref={inputRef}
                  type="email"
                  placeholder={LABEL_ENTER_EMAIL}
                  className="input-underline"
                  isInvalid={!!errors.email}
                  autoComplete="email"
                  maxLength={50}
                />
              )}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email?.message}
            </Form.Control.Feedback>
          </Form.Group>

          <div className="d-flex gap-2 justify-content-end">
            <Button
              variant="outline-secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={isSubmitting || !email?.trim()}
            >
              {isSubmitting ? (
                <>
                  Sending
                  <Spinner animation="border" size="sm" className="ms-2" />
                </>
              ) : (
                "Send"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default EmailPopup;
